import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';

export default function Categories() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const selectedLabel = selected
    ? t(`categoryNames.${selected}`)
    : t('categorySection.dropdownLabel');

  return (
    <section id="categories" className="px-4 md:px-6 py-12 md:py-16 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            {t('categorySection.title')}
          </h2>
          <p className="mt-3 text-foreground-600 text-sm md:text-base">{t('categorySection.subtitle')}</p>
        </div>

        <div ref={ref} className="relative mt-8 max-w-md mx-auto">
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="w-full flex items-center justify-between gap-3 px-5 py-3.5 rounded-lg border border-background-300 bg-background-50 text-left cursor-pointer transition-colors hover:border-foreground-400"
          >
            <span className="flex items-center gap-2 text-sm font-medium text-foreground-800">
              <i className="ri-grid-line text-foreground-500" />
              {selectedLabel}
            </span>
            <i className={`ri-arrow-down-s-line text-foreground-500 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>

          {open && (
            <div className="absolute left-0 right-0 mt-2 rounded-lg border border-background-200 bg-background-50 max-h-80 overflow-y-auto py-1.5 z-30">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setSelected(cat.id)}
                  className="flex items-center justify-between px-5 py-2.5 text-sm text-foreground-700 hover:bg-background-100 cursor-pointer transition-colors whitespace-nowrap"
                >
                  <span>{t(`categoryNames.${cat.id}`)}</span>
                  <i className="ri-arrow-right-up-line text-foreground-400" />
                </a>
              ))}
            </div>
          )}
        </div>

        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3 md:gap-5">
          {categories.map((cat) => (
            <a
              key={cat.id}
              href={cat.url}
              target="_blank"
              rel="noopener nofollow"
              className="group rounded-lg border border-background-200 bg-background-50 overflow-hidden cursor-pointer transition-all hover:border-primary-300"
            >
              <div className="w-full h-28 md:h-36 overflow-hidden">
                <img
                  src={cat.image}
                  alt={t(`categoryNames.${cat.id}`)}
                  title={t(`categoryNames.${cat.id}`)}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="px-3 py-3 flex items-center justify-between gap-1">
                <span className="text-xs md:text-sm font-medium text-foreground-900 whitespace-nowrap">
                  {t(`categoryNames.${cat.id}`)}
                </span>
                <i className="ri-arrow-right-up-line text-foreground-400 group-hover:text-primary-600 text-sm" />
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}